import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../../lib/api";
import { Eye, X, Printer, FileText, List, Lock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ThermalReceipt from "../../components/ThermalReceipt";
import RelatorioTermicoModal from "../../components/RelatorioTermicoModal";
import FecharCaixaModal from "../../components/FecharCaixaModal";
import ThermalReport from "../../components/ThermalReport";

const money = (v) => `R$ ${Number(v).toFixed(2).replace(".", ",")}`;

export default function Historico() {
  const { unidade, theme } = useOutletContext();
  const [vendas, setVendas] = useState([]);
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [detalhe, setDetalhe] = useState(null);
  const [relatorio, setRelatorio] = useState(null); // {mode, vendas, periodo}
  const [fechamentos, setFechamentos] = useState([]);
  const [sessao, setSessao] = useState(null);
  const [fechamentoOpen, setFechamentoOpen] = useState(false);
  const [fechamentoView, setFechamentoView] = useState(null); // fechamento selecionado para ver

  const hojeISO = () => new Date().toISOString().slice(0, 10);

  const loadFechamentos = async () => {
    const [fs, sa] = await Promise.all([
      api.get("/fechamentos", { params: { unidade_id: unidade.id } }),
      api.get("/fechamentos/sessao-aberta", { params: { unidade_id: unidade.id } }),
    ]);
    setFechamentos(fs.data);
    setSessao(sa.data);
  };

  const verFechamento = async (f) => {
    const r = await api.get(`/fechamentos/${f.id}`);
    setFechamentoView(r.data);
  };

  const gerarRelatorio = async (mode) => {
    // Usa filtro atual; se nenhuma data, usa hoje
    const inicio = dataInicio || hojeISO();
    const fim = dataFim || hojeISO();
    const r = await api.get("/vendas", {
      params: { unidade_id: unidade.id, data_inicio: inicio, data_fim: fim },
    });
    const sameDay = inicio === fim;
    setRelatorio({
      mode,
      vendas: r.data,
      periodo: sameDay
        ? new Date(inicio + "T00:00").toLocaleDateString("pt-BR")
        : `${new Date(inicio + "T00:00").toLocaleDateString("pt-BR")} → ${new Date(fim + "T00:00").toLocaleDateString("pt-BR")}`,
    });
  };

  const load = async () => {
    const params = { unidade_id: unidade.id };
    if (dataInicio) params.data_inicio = dataInicio;
    if (dataFim) params.data_fim = dataFim;
    const r = await api.get("/vendas", { params });
    setVendas(r.data);
  };

  const isOpenSession = (venda) => {
    if (!venda) return false;
    if (!sessao || !sessao.periodo_inicio) return true;
    return venda.data_venda > sessao.periodo_inicio;
  };

  const [confirmExcluir, setConfirmExcluir] = useState(null); // venda

  const excluirVenda = async (venda) => {
    if (!isOpenSession(venda)) {
      toast.error("Venda em caixa fechado — não pode ser excluída.");
      return;
    }
    try {
      await api.delete(`/vendas/${venda.id}`);
      toast.success("Venda excluída.");
      setConfirmExcluir(null);
      await Promise.all([load(), loadFechamentos()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir venda");
    }
  };

  useEffect(() => {
    load();
    loadFechamentos();
  }, [unidade.id]); // eslint-disable-line

  return (
    <div>
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">
          Registro protegido · Somente leitura
        </p>
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
          Histórico de Vendas
        </h2>
      </div>

      {/* Barra da sessão atual */}
      <div className="border-2 border-neutral-900 mb-6 p-4 flex flex-col sm:flex-row sm:items-center gap-3 bg-white">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Lock className="w-6 h-6 text-neutral-700 flex-shrink-0" strokeWidth={2.5} />
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
              Sessão atual {sessao?.periodo_inicio ? "· desde" : "· desde o início"}
            </div>
            <div className="font-display font-black text-lg truncate">
              {sessao?.periodo_inicio
                ? new Date(sessao.periodo_inicio).toLocaleString("pt-BR")
                : "Início dos registros"}
            </div>
            {sessao && (
              <div className="font-mono text-xs text-neutral-600 mt-0.5">
                {sessao.qtd_vendas} vendas · {sessao.qtd_itens} itens · Líquido{" "}
                <span className="font-bold">
                  R$ {Number(sessao.total_liquido).toFixed(2).replace(".", ",")}
                </span>
              </div>
            )}
          </div>
        </div>
        <button
          data-testid="fechar-caixa-btn"
          onClick={() => setFechamentoOpen(true)}
          disabled={!sessao || sessao.qtd_vendas === 0}
          className="bg-neutral-950 hover:bg-neutral-800 text-white h-11 px-5 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2 cta-brutal disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Lock className="w-4 h-4" /> Fechar Caixa Atual
        </button>
      </div>

      <div className="bg-white border-2 border-neutral-900 mb-6 p-4 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            Data início
          </label>
          <input
            data-testid="historico-data-inicio"
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
            className="h-10 px-3 border-2 border-neutral-900 font-mono text-sm w-full sm:w-auto"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            Data fim
          </label>
          <input
            data-testid="historico-data-fim"
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
            className="h-10 px-3 border-2 border-neutral-900 font-mono text-sm w-full sm:w-auto"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="historico-filtrar-btn"
            onClick={load}
            className={`${theme.primary} h-10 px-5 font-display font-bold uppercase text-xs tracking-widest`}
          >
            Aplicar Filtro
          </button>
          {(dataInicio || dataFim) && (
            <button
              onClick={() => {
                setDataInicio("");
                setDataFim("");
                setTimeout(load, 0);
              }}
              className="h-10 px-4 border-2 border-neutral-900 bg-white font-display font-bold uppercase text-xs tracking-widest"
            >
              Limpar
            </button>
          )}
          <button
            data-testid="historico-relatorio-resumido"
            onClick={() => gerarRelatorio("resumido")}
            className="h-10 px-4 border-2 border-neutral-900 bg-yellow-400 hover:bg-yellow-500 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2"
          >
            <FileText className="w-4 h-4" /> Resumo do Dia
          </button>
          <button
            data-testid="historico-relatorio-completo"
            onClick={() => gerarRelatorio("completo")}
            className="h-10 px-4 bg-neutral-950 hover:bg-neutral-800 text-white font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2"
          >
            <List className="w-4 h-4" /> Relatório Completo
          </button>
        </div>
      </div>

      <div className="bg-white border-2 border-neutral-900 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-neutral-300 bg-neutral-50">
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest">Data</th>
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest">Vendedor</th>
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest hidden sm:table-cell">Cliente</th>
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest hidden md:table-cell">Placa</th>
              <th className="text-right p-3 font-mono text-[10px] uppercase tracking-widest">Total</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {vendas.map((v) => (
              <tr
                key={v.id}
                data-testid={`historico-row-${v.id}`}
                className="border-b border-neutral-100 hover:bg-neutral-50"
              >
                <td className="p-3 font-mono text-xs">
                  {new Date(v.data_venda).toLocaleString("pt-BR")}
                </td>
                <td className="p-3 text-sm font-bold">{v.vendedor_nome}</td>
                <td className="p-3 text-sm hidden sm:table-cell">{v.cliente_nome || "—"}</td>
                <td className="p-3 font-mono text-xs hidden md:table-cell">{v.placa || "—"}</td>
                <td className="p-3 text-right font-mono font-bold">{money(v.total_liquido)}</td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <button
                      data-testid={`historico-detalhe-${v.id}`}
                      onClick={() => setDetalhe(v)}
                      className="p-2 border border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
                      title="Ver detalhes"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {isOpenSession(v) ? (
                      <button
                        data-testid={`historico-excluir-${v.id}`}
                        onClick={() => setConfirmExcluir(v)}
                        className="p-2 border border-neutral-900 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
                        title="Excluir venda (sessão aberta)"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    ) : (
                      <span
                        data-testid={`historico-venda-fechada-${v.id}`}
                        className="p-2 border border-neutral-200 text-neutral-300"
                        title="Venda em caixa fechado — não pode ser excluída"
                      >
                        <Lock className="w-4 h-4" />
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {vendas.length === 0 && (
              <tr>
                <td colSpan={6} className="p-8 text-center text-sm text-neutral-500">
                  Nenhuma venda encontrada
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {detalhe && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border-4 border-neutral-900 max-w-lg w-full max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b-2 border-neutral-900 print:hidden">
              <h3 className="font-display font-bold uppercase">Detalhes da Venda</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => window.print()}
                  className={`${theme.primary} h-10 px-3 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2`}
                >
                  <Printer className="w-4 h-4" /> Imprimir
                </button>
                <button
                  data-testid="historico-fechar-detalhe"
                  onClick={() => setDetalhe(null)}
                  className="p-2 border-2 border-neutral-900 hover:bg-neutral-900 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="p-4">
              <ThermalReceipt venda={detalhe} unidade={unidade} />
            </div>
          </div>
        </div>
      )}

      <RelatorioTermicoModal
        open={!!relatorio}
        onClose={() => setRelatorio(null)}
        titulo={`${unidade.nome} — Relatório ${relatorio?.mode === "resumido" ? "Resumido" : "Completo"}`}
        escopo={unidade.nome}
        periodo={relatorio?.periodo}
        vendas={relatorio?.vendas || []}
        mode={relatorio?.mode || "completo"}
        hideProfitCost
      />

      <FecharCaixaModal
        open={fechamentoOpen}
        onClose={() => setFechamentoOpen(false)}
        unidade={unidade}
        onClosed={() => {
          loadFechamentos();
          load();
        }}
      />

      {/* Modal confirmação exclusão (substitui window.confirm que falha em iframe) */}
      {confirmExcluir && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border-4 border-neutral-900 max-w-md w-full">
            <div className="p-4 border-b-2 border-neutral-900 bg-red-600 text-white">
              <h3 className="font-display font-black text-lg uppercase tracking-tight">
                Excluir venda?
              </h3>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm">
                Você está prestes a excluir esta venda da sessão atual:
              </p>
              <div className="bg-neutral-50 border-2 border-neutral-900 p-3 font-mono text-xs space-y-1">
                <div>Data: {new Date(confirmExcluir.data_venda).toLocaleString("pt-BR")}</div>
                <div>Vendedor: <span className="font-bold">{confirmExcluir.vendedor_nome}</span></div>
                {confirmExcluir.cliente_nome && (
                  <div>Cliente: {confirmExcluir.cliente_nome}</div>
                )}
                <div>
                  Total:{" "}
                  <span className="font-bold">{money(confirmExcluir.total_liquido)}</span>
                </div>
              </div>
              <p className="text-xs text-neutral-600">
                Como o estoque só é descontado no fechamento de caixa, nenhum
                ajuste de estoque é necessário.
              </p>
            </div>
            <div className="p-4 border-t-2 border-neutral-900 flex justify-end gap-2">
              <button
                data-testid="historico-cancelar-excluir"
                onClick={() => setConfirmExcluir(null)}
                className="h-10 px-4 border-2 border-neutral-900 bg-white font-display font-bold uppercase text-xs tracking-widest"
              >
                Cancelar
              </button>
              <button
                data-testid="historico-confirmar-excluir"
                onClick={() => excluirVenda(confirmExcluir)}
                className="bg-red-600 hover:bg-red-700 text-white h-10 px-5 font-display font-bold uppercase text-xs tracking-widest cta-brutal"
              >
                Sim, excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lista de fechamentos anteriores */}
      {fechamentos.length > 0 && (
        <div className="mt-8">
          <div className="mb-4 flex items-center gap-2">
            <Lock className="w-4 h-4" />
            <h3 className="font-display font-bold text-lg uppercase tracking-tight">
              Fechamentos Anteriores
            </h3>
          </div>
          <div className="bg-white border-2 border-neutral-900 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-neutral-300 bg-neutral-50">
                  <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest">#</th>
                  <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest">Fechado em</th>
                  <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest hidden md:table-cell">Início do período</th>
                  <th className="text-right p-3 font-mono text-[10px] uppercase tracking-widest">Vendas</th>
                  <th className="text-right p-3 font-mono text-[10px] uppercase tracking-widest hidden md:table-cell">Itens</th>
                  <th className="text-right p-3 font-mono text-[10px] uppercase tracking-widest">Líquido</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {fechamentos.map((f) => (
                  <tr
                    key={f.id}
                    data-testid={`fechamento-row-${f.id}`}
                    className="border-b border-neutral-100 hover:bg-neutral-50"
                  >
                    <td className="p-3 font-mono font-bold">#{f.numero}</td>
                    <td className="p-3 font-mono text-xs">
                      {new Date(f.fechado_em).toLocaleString("pt-BR")}
                    </td>
                    <td className="p-3 font-mono text-xs text-neutral-500 hidden md:table-cell">
                      {f.periodo_inicio
                        ? new Date(f.periodo_inicio).toLocaleString("pt-BR")
                        : "—"}
                    </td>
                    <td className="p-3 text-right font-mono text-sm">{f.qtd_vendas}</td>
                    <td className="p-3 text-right font-mono text-sm hidden md:table-cell">
                      {f.qtd_itens}
                    </td>
                    <td className="p-3 text-right font-mono font-bold text-sm">
                      R$ {Number(f.total_liquido).toFixed(2).replace(".", ",")}
                    </td>
                    <td className="p-3 text-right">
                      <button
                        data-testid={`fechamento-view-${f.id}`}
                        onClick={() => verFechamento(f)}
                        className="p-2 border border-neutral-900 hover:bg-neutral-900 hover:text-white"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Visualizar fechamento passado */}
      {fechamentoView && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border-4 border-neutral-900 w-full max-w-md max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b-2 border-neutral-900 print:hidden">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                  Fechamento #{fechamentoView.numero}
                </p>
                <h3 className="font-display font-black text-lg">
                  {new Date(fechamentoView.fechado_em).toLocaleString("pt-BR")}
                </h3>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => window.print()}
                  className="bg-yellow-400 text-neutral-950 h-10 px-4 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2"
                >
                  <Printer className="w-4 h-4" /> Imprimir
                </button>
                <button
                  data-testid="fechamento-view-close"
                  onClick={() => setFechamentoView(null)}
                  className="p-2 border-2 border-neutral-900 hover:bg-neutral-900 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4 bg-neutral-50">
              <ThermalReport
                titulo={`${unidade.nome} — Fechamento #${fechamentoView.numero}`}
                escopo={unidade.nome}
                periodo={`${
                  fechamentoView.periodo_inicio
                    ? new Date(fechamentoView.periodo_inicio).toLocaleString("pt-BR")
                    : "Início"
                } → ${new Date(fechamentoView.fechado_em).toLocaleString("pt-BR")}`}
                vendas={fechamentoView.vendas_snapshot || []}
                mode="completo"
                hideProfitCost
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
